import styled from '@emotion/styled';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useSelect } from 'downshift';
import React from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@/features/common';
import { useLogout } from '@/features/toggl-accounts';
import { useWorkspaceId } from '@/hooks/router';
import { useGetMe } from '@/queries/me/queries';

export function WorkspaceSelector() {
  const workspaceId = useWorkspaceId();
  const meQuery = useGetMe();
  const logout = useLogout();

  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps } =
    useSelect({
      items: meQuery.isSuccess ? (meQuery.data.workspaces ?? []) : [],
      itemToString: (workspace) => workspace?.name ?? '',
    });

  return (
    <div>
      <WorkspacesSelector type="button" {...getToggleButtonProps()}>
        <span>
          {
            meQuery.data?.workspaces?.find(
              (workspace) => String(workspace.id) === workspaceId
            )?.name
          }
        </span>
        <Icon icon={solid('chevron-down')} color="purple-60" fontSize="14px" />
      </WorkspacesSelector>

      <WorkspacesListContainer {...getMenuProps()} visible={isOpen}>
        {isOpen && (
          <>
            <WorkspacesListHeader>Workspaces</WorkspacesListHeader>
            <WorkspacesList>
              {meQuery.data?.workspaces?.map((item, index) => (
                <WorkspacesListItem
                  key={String(item.id)}
                  {...getItemProps({ item, index })}
                >
                  <Link to={`/admin/${String(item.id)}`}>
                    <span>{item.name}</span>
                  </Link>
                  {String(item.id) === workspaceId ? (
                    <Icon icon={solid('check')} color="orange-100" />
                  ) : null}
                </WorkspacesListItem>
              ))}
            </WorkspacesList>
            <SignOutButton type="button" onClick={() => logout()}>
              Sign out
            </SignOutButton>
          </>
        )}
      </WorkspacesListContainer>
    </div>
  );
}

const WorkspacesSelector = styled.button`
  border: 0;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  height: 48px;
  width: 100%;
  cursor: pointer;
  background-color: #ffffff;

  &:hover {
    background-color: #F5F55;
  }

  span {
    font-size: 16px;
    font-weight: 500;
    color: #010101;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const WorkspacesListContainer = styled.div<{ visible: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.16);
  border-radius: 6px;
  padding: 4px;
  position: absolute;
  top: 50px;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  width: 240px;
  z-index: 10;
`;

const WorkspacesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const WorkspacesListHeader = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.typography.colorSecondary};
  display: flex;
  font-size: 10px;
  font-weight: 500;
  height: 28px;
  padding: 0 12px;
  text-transform: uppercase;
`;

const WorkspacesListItem = styled.li`
  align-items: center;
  border-radius: 6px;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: 500;
  justify-content: space-between;
  padding: 0 12px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.purple[5]};
  }

  a {
    color: ${({ theme }) => theme.typography.colorPrimary};
    display: flex;
    align-items: center;
    height: 28px;
    width: 100%;
  }

  span {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 186px;
  }
`;

const SignOutButton = styled.button`
  background: transparent;
  border-radius: 6px;
  border: none;
  color: ${({ theme }) => theme.colors.red[100]};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  height: 28px;
  margin: 0;
  padding: 0 12px;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.colors.red[10]};
  }
`;
